import { DriverSocketMsgType } from "@libs/types/message"
import { RoYuanDevice, type VK } from "../define/define"
import type { PostWatchKey } from "../define/request"
import { WatchType } from "../define/request"
import { fakeSocket } from '../fakesocket/fakesocket'
import { keyCodeToVKMap, mouseKeyToCodeMap } from "./key"
import { deviceManager } from "@hid/device/device_manager"

export interface HeldKey {
    Key: VK
    Press: boolean
    Delay: number
}

export enum RawKeycodeSequenceAction {
    Down,
    Up,
    Delay,
}

interface NavigatorWithKeyboard extends Navigator {
    keyboard?: {
        lock: () => Promise<void>
    }
}

class KeycodeRecorder {
    private respTotal: HeldKey[] = []
    private heldKey: HeldKey = { Key: 0 as VK, Press: false, Delay: 0 }
    private lastEvtTime: number = 0


    constructor() { }

    private updateDisplay(): void {
        fakeSocket.sendMsgData(DriverSocketMsgType.MSWatchKey, this.heldKey)
    }

    private recordKeyAction(code: VK, action: RawKeycodeSequenceAction): void {
        const currentTime = Date.now()
        let delay = 0
        if (this.respTotal.length > 0) {
            delay = currentTime - this.lastEvtTime
            this.respTotal[this.respTotal.length - 1].Delay = delay
        }
        const record = { Key: code, Press: action === RawKeycodeSequenceAction.Down, Delay: 0 }
        this.respTotal.push(record)
        this.heldKey = { ...record, Delay: delay }
        this.lastEvtTime = currentTime
        this.updateDisplay()
    }

    private handleMouseDown = (event: MouseEvent): void => {
        event.preventDefault()
        event.stopPropagation(); // 阻止事件冒泡
        const keycode = mouseKeyToCodeMap[event.button]
        if (keycode !== undefined) {
            this.recordKeyAction(keycode as VK, RawKeycodeSequenceAction.Down)
        }
    }
    private handleMouseUp = (event: MouseEvent): void => {
        event.preventDefault()
        event.stopPropagation();
        const keycode = mouseKeyToCodeMap[event.button]
        if (keycode !== undefined) {
            this.recordKeyAction(keycode as VK, RawKeycodeSequenceAction.Up)
        }
    }
    private handleContextMenu = (event: MouseEvent): void => {
        event.preventDefault()
        event.stopPropagation();
    }
    // private handleMouseWheel = (event: WheelEvent): void => {
    //     event.preventDefault()
    //     console.log("keycode", event)
    //     const keycode = mouseKeyToCodeMap[event.deltaY > 0 ? 0x10 : 0x11]
    //     if (keycode !== undefined) {
    //         this.recordKeyAction(keycode as VK, RawKeycodeSequenceAction.Up)
    //         requestAnimationFrame(() => {
    //             this.recordKeyAction(keycode as VK, RawKeycodeSequenceAction.Down)
    //         })
    //     }
    // }
    private handleKeyDown = (event: KeyboardEvent): void => {
        event.preventDefault() // 阻止默认行为
        const keycode = keyCodeToVKMap[event.code]
        if (keycode !== undefined && !event.repeat) {
            this.recordKeyAction(keycode as VK, RawKeycodeSequenceAction.Down)
        }
    }

    private handleKeyUp = (event: KeyboardEvent): void => {
        event.preventDefault() // 阻止默认行为
        const keycode = keyCodeToVKMap[event.code]
        if (keycode !== undefined) {
            this.recordKeyAction(keycode as VK, RawKeycodeSequenceAction.Up)
        }
    }

    private async lockKeyboard() {
        const nav = navigator as NavigatorWithKeyboard
        if (nav.keyboard && nav.keyboard.lock) {
            try {
                await nav.keyboard.lock()
            } catch (err) {
                console.error("Failed to lock keyboard:", err)
            }
        }
    }

    private async requestFullscreen(element: HTMLElement) {
        if (element.requestFullscreen) {
            await element.requestFullscreen()
        } else if ((element as any).webkitRequestFullscreen) { // Safari
            await (element as any).webkitRequestFullscreen()
        } else if ((element as any).msRequestFullscreen) { // IE/Edge
            await (element as any).msRequestFullscreen()
        }
    }

    private async exitFullscreen() {
        try {
            if (document.exitFullscreen) {
                await document.exitFullscreen()
            } else if ((document as any).webkitExitFullscreen) {
                await (document as any).webkitExitFullscreen()
            } else if ((document as any).msExitFullscreen) {
                await (document as any).msExitFullscreen()
            }
        } catch (error) {
            console.error("退出全屏失败:", error)
        }
    }

    public EnableRecording(data: PostWatchKey): HeldKey[] {
        if (data.WatchType === WatchType.Start) {
            window.addEventListener("keydown", this.handleKeyDown)
            window.addEventListener("keyup", this.handleKeyUp)
            if (RoYuanDevice.includes(deviceManager.deviceID)) {
                window.addEventListener('mouseup', this.handleMouseUp)
                window.addEventListener('mousedown', this.handleMouseDown)
                window.addEventListener('contextmenu', this.handleContextMenu)
                // window.addEventListener('wheel', this.handleMouseWheel, { passive: false })
            }
            try {
                this.requestFullscreen(document.documentElement).then(() => this.lockKeyboard())
            } catch (error) {
                console.error("Failed to enter fullscreen:", error)
            }
            return []
        } else {
            const result = this.respTotal.slice()
            window.removeEventListener("keydown", this.handleKeyDown)
            window.removeEventListener("keyup", this.handleKeyUp)
            if (RoYuanDevice.includes(deviceManager.deviceID)) {
                window.removeEventListener('mouseup', this.handleMouseUp)
                window.removeEventListener('mousedown', this.handleMouseDown)
                window.removeEventListener('contextmenu', this.handleContextMenu)
                // window.removeEventListener('wheel', this.handleMouseWheel)
            }
            this.respTotal = []
            this.heldKey = { Key: 0 as VK, Press: false, Delay: 0 }
            this.lastEvtTime = 0
            this.exitFullscreen()
            return result
        }
    }
}

export const keycodeRecorder = new KeycodeRecorder()
